import React from 'react';

import { Divider, Grid, Header } from '@mairistem/composants';

import { PanelButtonEdition } from '@mairistem/administres-utils';
import ShowDiffContent from '@/components/Common/ShowDiffContent';
import { formatLieu } from '@/utils';
import type { ConventionDeclaration } from '@/types';

import InformationsEnregistrementDeclarationModal from './InformationsEnregistrementDeclarationModal';

// Nom et prénom de l'officier, « Non renseigné » si aucun des deux n'est saisi.
const formatOfficier = (convention: any): string =>
  [convention?.signataireNomFamille, convention?.signatairePrenom]
    .map((valeur) => String(valeur ?? '').trim())
    .filter(Boolean)
    .join(' ') || 'Non renseigné';

interface InformationsEnregistrementDeclarationProps {
  showDiff?: boolean;
  conventionDeclaration: any;
  updated?: boolean;
  onChange?: (data: Partial<ConventionDeclaration>) => void;
  onSave?: () => Promise<void>;
  onRollback?: () => void;
}

const InformationsEnregistrementDeclaration = (
  props: InformationsEnregistrementDeclarationProps,
): JSX.Element => {
  const {
    conventionDeclaration,
    showDiff,
    updated,
    onChange,
    onSave,
    onRollback,
  } = props;

  const [modalEditionOpen, setModalEditionOpen] =
    React.useState<boolean>(false);

  const handleClose = React.useCallback(() => {
    setModalEditionOpen(false);
  }, []);

  const handleCancel = React.useCallback(() => {
    onRollback?.();
    handleClose();
  }, [handleClose, onRollback]);

  const adressePacs = formatLieu(
    conventionDeclaration?.adressePacs?.localite,
    conventionDeclaration?.adressePacs?.codePostal,
    conventionDeclaration?.adressePacs?.libellePays,
  );
  const prevAdressePacs = formatLieu(
    conventionDeclaration?.previousConvention?.adressePacs?.localite,
    conventionDeclaration?.previousConvention?.adressePacs?.codePostal,
    conventionDeclaration?.previousConvention?.adressePacs?.libellePays,
  );

  return (
    <Grid>
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            OFFICIER D&apos;ÉTAT CIVIL
          </Header>
          <PanelButtonEdition
            onClick={() => setModalEditionOpen(true)}
            data-testid="panel-edit-modal-enregistrement-declaration"
          />
        </div>
        {modalEditionOpen && (
          <InformationsEnregistrementDeclarationModal
            signataireNomFamille={conventionDeclaration?.signataireNomFamille}
            signatairePrenom={conventionDeclaration?.signatairePrenom}
            signataireQualite={conventionDeclaration?.signataireQualite}
            statut={conventionDeclaration?.statut}
            adressePacs={conventionDeclaration?.adressePacs}
            typeComparution={conventionDeclaration?.typeComparution}
            commentaire={conventionDeclaration?.commentaire}
            updated={updated ?? false}
            onChange={onChange ?? (() => {})}
            onSave={onSave ?? (() => Promise.resolve())}
            onClose={handleClose}
            onCancel={handleCancel}
          />
        )}
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Nom et prénom</b>
              </span>
              <ShowDiffContent
                oldContent={formatOfficier(
                  conventionDeclaration?.previousConvention,
                )}
                newContent={formatOfficier(conventionDeclaration)}
              />
            </>
          ) : (
            <>
              <span>
                <b>Nom et prénom : </b>
              </span>
              <span>{formatOfficier(conventionDeclaration)}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Grade signataire</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention
                    ?.signataireQualite || 'Non renseigné'
                }
                newContent={
                  conventionDeclaration?.signataireQualite || 'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Grade signataire : </b>
              </span>
              <span>
                {conventionDeclaration?.signataireQualite || 'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            LIEU D&apos;ENREGISTREMENT
          </Header>
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Lieu d&apos;enregistrement</b>
              </span>
              <ShowDiffContent
                oldContent={prevAdressePacs}
                newContent={adressePacs}
              />
            </>
          ) : (
            <>
              <span>
                <b>Lieu d&apos;enregistrement : </b>
              </span>
              <span>{adressePacs}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Commune déléguée</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention?.adressePacs
                    ?.communeDelegue || 'Non renseigné'
                }
                newContent={
                  conventionDeclaration?.adressePacs?.communeDelegue ||
                  'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Commune déléguée : </b>
              </span>
              <span>
                {conventionDeclaration?.adressePacs?.communeDelegue ||
                  'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
        {conventionDeclaration?.statut !== 'Dissous' && (
          <Grid.Column width={5}>
            {showDiff ? (
              <>
                <span>
                  <b>Type de comparution</b>
                </span>
                <ShowDiffContent
                  oldContent={
                    conventionDeclaration?.previousConvention
                      ?.typeComparution || 'Non renseigné'
                  }
                  newContent={
                    conventionDeclaration?.typeComparution || 'Non renseigné'
                  }
                />
              </>
            ) : (
              <>
                <span>
                  <b>Type de comparution : </b>
                </span>
                <span>
                  {conventionDeclaration?.typeComparution || 'Non renseigné'}
                </span>
              </>
            )}
          </Grid.Column>
        )}
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            COMMENTAIRE
          </Header>
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>
          {showDiff ? (
            <>
              <span>
                <b>Commentaire</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention?.commentaire ||
                  'Non renseigné'
                }
                newContent={
                  conventionDeclaration?.commentaire || 'Non renseigné'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Commentaire : </b>
              </span>
              <span>
                {conventionDeclaration?.commentaire || 'Non renseigné'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default InformationsEnregistrementDeclaration;
